import * as React from "react";
// import * as localForage from "localforage";

import {
  useMyBagPresentationModel,
  useMyBagUserExperience,
  useReactMyBagInitialState,
} from "../providers";
import {
  useBrowserInformation,
  useCustomerCartId,
  useCustomerSession,
} from "../../Common";
import { readLegacyCheckoutConfig } from "../../Common/legacy-checkout-config";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "apple-pay-button": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          buttonstyle?: string;
          type?: string;
          locale?: string;
        },
        HTMLElement
      >;
    }
  }
}

export const MyBagSummary_ActionsExpressCheckout: React.FunctionComponent =
  () => {
    const { initialState } = useReactMyBagInitialState();
    const { isApplePayEnabled, isGooglePayEnabled } = initialState;
    const storeCode = String(initialState.store_code ?? "");
    const legacyConfig = readLegacyCheckoutConfig();
    const currencyCode = legacyConfig?.quoteData?.quote_currency_code;
    const googlePayConfig = legacyConfig?.payment?.adyenGooglePay;

    const { canIProceedToCheckout } = useMyBagUserExperience();
    const { presentationMyBagItems } = useMyBagPresentationModel();
    const hasUnavailableItems = presentationMyBagItems.some(
      (item) => item.isUnavailable
    );

    const { isCustomerLoggedIn } = useCustomerSession();
    const { cartId } = useCustomerCartId();

    const { isSafari } = useBrowserInformation();

    const [isApplePayButtonVisible, setIsApplePayButtonVisible] =
      React.useState(false);
    const [isGooglePayButtonVisible, setIsGooglePayButtonVisible] =
      React.useState(false);

    const onMountExternalPayButton = React.useCallback((element: any) => {
      // (window as any).bbntCheckoutExternalApplicationLocalForage = localForage;
      (window as any).require(["jquery"], function ($: any) {
        const $element = $(element);
        if ($element) {
          $element.trigger("contentUpdated");
        }
      });
    }, []);

    const canApplePay = React.useMemo(
      () => !!(isApplePayEnabled && storeCode && currencyCode),
      [isApplePayEnabled, storeCode, currencyCode]
    );

    const applePay = React.useMemo(() => {
      return (
        <React.Fragment>
          {canApplePay && (
            <apple-pay-button
              ref={onMountExternalPayButton}
              buttonstyle="black"
              type="check-out"
              locale="en"
              data-mage-init={`{
                  "newCheckoutApplePayButton": {
                    "cartId": "${cartId}",
                    "isLoggedIn": ${isCustomerLoggedIn},
                    "storeCode": "${storeCode}",
                    "currencyCode": "${currencyCode}"
                  }
                }`}
            ></apple-pay-button>
          )}
        </React.Fragment>
      );
    }, [
      canApplePay,
      cartId,
      isCustomerLoggedIn,
      onMountExternalPayButton,
      storeCode,
      currencyCode,
    ]);

    const canGooglePay = React.useMemo(
      () =>
        !!(
          !isSafari &&
          isGooglePayEnabled &&
          storeCode &&
          currencyCode &&
          typeof googlePayConfig?.checkoutEnvironment === "string" &&
          googlePayConfig.checkoutEnvironment.length > 0 &&
          typeof googlePayConfig?.merchantIdentifier === "string" &&
          googlePayConfig.merchantIdentifier.length > 0 &&
          typeof googlePayConfig?.merchantAccount === "string" &&
          googlePayConfig.merchantAccount.length > 0
        ),
      [
        isSafari,
        isGooglePayEnabled,
        storeCode,
        currencyCode,
        googlePayConfig?.checkoutEnvironment,
        googlePayConfig?.merchantIdentifier,
        googlePayConfig?.merchantAccount,
      ]
    );

    const googlePay = React.useMemo(() => {
      return (
        <React.Fragment>
          {canGooglePay && (
            <div
              ref={onMountExternalPayButton}
              className="google-pay-container"
              data-mage-init={`{
                  "newCheckoutGooglePayButton": {
                    "cartId": "${cartId}",
                    "isLoggedIn": ${isCustomerLoggedIn},
                    "storeCode": "${storeCode}",
                    "currencyCode": "${currencyCode}",
                    "gpayEnvironment": "${googlePayConfig?.checkoutEnvironment}",
                    "merchantId": "${googlePayConfig?.merchantIdentifier}",
                    "merchantName": "${googlePayConfig?.merchantAccount}"
                  }
                }`}
            ></div>
          )}
        </React.Fragment>
      );
    }, [
      canGooglePay,
      onMountExternalPayButton,
      cartId,
      isCustomerLoggedIn,
      storeCode,
      currencyCode,
      googlePayConfig?.checkoutEnvironment,
      googlePayConfig?.merchantIdentifier,
      googlePayConfig?.merchantAccount,
    ]);

    const handleApplePayButtonVisiblityChange = React.useCallback(
      (event) => {
        setIsApplePayButtonVisible(event.detail.isVisible);
      },
      [setIsApplePayButtonVisible]
    );
    React.useEffect(() => {
      window.addEventListener(
        "bbnt-apple-pay-button-visibility-changed",
        handleApplePayButtonVisiblityChange
      );
      return () => {
        window.removeEventListener(
          "bbnt-apple-pay-button-visibility-changed",
          handleApplePayButtonVisiblityChange
        );
      };
    }, [handleApplePayButtonVisiblityChange]);

    const handleGooglePayButtonVisiblityChange = React.useCallback(
      (event) => {
        setIsGooglePayButtonVisible(event.detail.isVisible);
      },
      [setIsGooglePayButtonVisible]
    );
    React.useEffect(() => {
      window.addEventListener(
        "bbnt-google-pay-button-visibility-changed",
        handleGooglePayButtonVisiblityChange
      );
      return () => {
        window.removeEventListener(
          "bbnt-google-pay-button-visibility-changed",
          handleGooglePayButtonVisiblityChange
        );
      };
    }, [handleGooglePayButtonVisiblityChange]);

    if (
      !canIProceedToCheckout ||
      hasUnavailableItems ||
      !(canApplePay || canGooglePay)
    ) {
      return null;
    }

    return (
      <section
        className={`express-checkout ${
          isApplePayButtonVisible || isGooglePayButtonVisible
            ? "visible"
            : "invisible"
        }`}
      >
        <p>{`Or use express checkout:`}</p>
        {applePay}
        {googlePay}
      </section>
    );
  };
